import { notEmpty } from '@ps/web-core-utils';
import classNames from 'clsx';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  MarketLevelCompoundMarketTemplateProps,
  MarketTemplateComponent
} from '@swf/sports/types/templates';

import styles from './GameLines.scss';

const GameLines: MarketTemplateComponent<MarketLevelCompoundMarketTemplateProps> = ({
  marketIds
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const allMarkets = useSelector(widgetSelect.markets);
  const events = useSelector(widgetSelect.events);

  const {
    overUnderMarket,
    moneyLineMarket,
    handicapMarket,
    firstMoneyLineRunner,
    firstHandicapRunner,
    secondMoneyLineRunner,
    secondHandicapRunner,
    overRunner,
    underRunner
  } = useMemo(() => {
    const mktIds = new Set(marketIds.filter(notEmpty));
    const filteredMarkets = Object.values(allMarkets).filter(
      (market) => market && mktIds.has(market.marketId)
    );

    const overUnderMarket = filteredMarkets.find(
      (mkt) => mkt?.marketType === 'TOTAL_POINTS_(OVER/UNDER)'
    );
    const moneyLineMarket = filteredMarkets.find((mkt) => mkt?.marketType === 'MONEY_LINE');
    const handicapMarket = filteredMarkets.find(
      (mkt) => mkt?.marketType === 'MATCH_HANDICAP_(2-WAY)'
    );

    const resultTypes = ['HOME', 'AWAY'];
    const moneyLineRunners = moneyLineMarket?.runners.filter((runner) =>
      resultTypes.includes(runner.resultType ?? '')
    );
    const handicapRunners = handicapMarket?.runners.filter((runner) =>
      resultTypes.includes(runner.resultType ?? '')
    );

    return {
      overUnderMarket,
      moneyLineMarket,
      handicapMarket,
      firstMoneyLineRunner: moneyLineRunners?.[0],
      firstHandicapRunner: handicapRunners?.[0],
      secondMoneyLineRunner: moneyLineRunners?.[1],
      secondHandicapRunner: handicapRunners?.[1],
      overRunner: overUnderMarket?.runners.find((runner) => runner.resultType === 'OVER'),
      underRunner: overUnderMarket?.runners.find((runner) => runner.resultType === 'UNDER')
    };
  }, [allMarkets, marketIds]);

  return (
    <table
      className={classNames(styles.table, {
        [styles.participantMarket]: (moneyLineMarket || handicapMarket) && !overUnderMarket,
        [styles.pointsMarket]: !moneyLineMarket && !handicapMarket && overUnderMarket,
        [styles.allParticipantMarkets]: moneyLineMarket && handicapMarket && !overUnderMarket,
        [styles.participantAndPointsMarkets]:
          (handicapMarket || moneyLineMarket) && overUnderMarket,
        [styles.allMarkets]: handicapMarket && moneyLineMarket && overUnderMarket
      })}
    >
      <thead>
        <tr className={styles.header}>
          <td className={styles.hidden} />
          {moneyLineMarket ? <th scope="col">{moneyLineMarket.marketName}</th> : null}
          {handicapMarket ? <th scope="col">{handicapMarket.marketName}</th> : null}
          {overUnderMarket ? (
            <th className={styles.overUnderHeader} scope="col">
              {overUnderMarket.marketName}
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {moneyLineMarket || handicapMarket ? (
          <>
            <tr className={styles.away}>
              <th scope="row">
                {firstMoneyLineRunner?.runnerName || firstHandicapRunner?.runnerName}
              </th>
              {moneyLineMarket && firstMoneyLineRunner ? (
                <td>
                  <Selection
                    competitionId={moneyLineMarket.competitionId}
                    eventId={moneyLineMarket.eventId}
                    eventTypeId={moneyLineMarket.eventTypeId}
                    handicap={firstMoneyLineRunner.handicap}
                    legTypes={moneyLineMarket.legTypes}
                    marketId={moneyLineMarket.marketId}
                    marketStatus={moneyLineMarket.marketStatus}
                    marketType={moneyLineMarket.marketType}
                    odds={firstMoneyLineRunner.winRunnerOdds}
                    runnerResultType={firstMoneyLineRunner.resultType}
                    runnerStatus={firstMoneyLineRunner.runnerStatus}
                    selectionId={firstMoneyLineRunner.selectionId}
                    startTime={events[moneyLineMarket.eventId].eventStartTime}
                  />
                </td>
              ) : null}
              {handicapMarket && firstHandicapRunner ? (
                <td>
                  <Selection
                    competitionId={handicapMarket.competitionId}
                    eventId={handicapMarket.eventId}
                    eventTypeId={handicapMarket.eventTypeId}
                    handicap={firstHandicapRunner.handicap}
                    legTypes={handicapMarket.legTypes}
                    marketId={handicapMarket.marketId}
                    marketStatus={handicapMarket.marketStatus}
                    marketType={handicapMarket.marketType}
                    odds={firstHandicapRunner.winRunnerOdds}
                    runnerResultType={firstHandicapRunner.resultType}
                    runnerStatus={firstHandicapRunner.runnerStatus}
                    selectionId={firstHandicapRunner.selectionId}
                    startTime={events[handicapMarket.eventId].eventStartTime}
                  />
                </td>
              ) : null}
              <td className={styles.hidden} />
            </tr>

            <tr className={styles.home}>
              <th scope="row">
                {secondMoneyLineRunner?.runnerName || secondHandicapRunner?.runnerName}
              </th>
              {moneyLineMarket && secondMoneyLineRunner ? (
                <td>
                  <Selection
                    competitionId={moneyLineMarket.competitionId}
                    eventId={moneyLineMarket.eventId}
                    eventTypeId={moneyLineMarket.eventTypeId}
                    handicap={secondMoneyLineRunner.handicap}
                    legTypes={moneyLineMarket.legTypes}
                    marketId={moneyLineMarket.marketId}
                    marketStatus={moneyLineMarket.marketStatus}
                    marketType={moneyLineMarket.marketType}
                    odds={secondMoneyLineRunner.winRunnerOdds}
                    runnerResultType={secondMoneyLineRunner.resultType}
                    runnerStatus={secondMoneyLineRunner.runnerStatus}
                    selectionId={secondMoneyLineRunner.selectionId}
                    startTime={events[moneyLineMarket.eventId].eventStartTime}
                  />
                </td>
              ) : null}
              {handicapMarket && secondHandicapRunner ? (
                <td>
                  <Selection
                    competitionId={handicapMarket.competitionId}
                    eventId={handicapMarket.eventId}
                    eventTypeId={handicapMarket.eventTypeId}
                    handicap={secondHandicapRunner.handicap}
                    legTypes={handicapMarket.legTypes}
                    marketId={handicapMarket.marketId}
                    marketStatus={handicapMarket.marketStatus}
                    marketType={handicapMarket.marketType}
                    odds={secondHandicapRunner.winRunnerOdds}
                    runnerResultType={secondHandicapRunner.resultType}
                    runnerStatus={secondHandicapRunner.runnerStatus}
                    selectionId={secondHandicapRunner.selectionId}
                    startTime={events[handicapMarket.eventId].eventStartTime}
                  />
                </td>
              ) : null}
              <td className={styles.hidden} />
            </tr>
          </>
        ) : null}

        {overUnderMarket ? (
          <tr className={styles.over}>
            <th scope="row">Over</th>
            {moneyLineMarket ? <td className={styles.hidden} /> : null}
            {handicapMarket ? <td className={styles.hidden} /> : null}
            <td>
              {overRunner ? (
                <Selection
                  competitionId={overUnderMarket.competitionId}
                  eventId={overUnderMarket.eventId}
                  eventTypeId={overUnderMarket.eventTypeId}
                  handicap={overRunner.handicap}
                  legTypes={overUnderMarket.legTypes}
                  marketId={overUnderMarket.marketId}
                  marketStatus={overUnderMarket.marketStatus}
                  marketType={overUnderMarket.marketType}
                  odds={overRunner.winRunnerOdds}
                  runnerResultType={overRunner.resultType}
                  runnerStatus={overRunner.runnerStatus}
                  selectionId={overRunner.selectionId}
                  startTime={events[overUnderMarket.eventId].eventStartTime}
                />
              ) : null}
            </td>
          </tr>
        ) : null}

        {overUnderMarket ? (
          <tr className={styles.under}>
            <th scope="row">Under</th>
            {moneyLineMarket ? <td className={styles.hidden} /> : null}
            {handicapMarket ? <td className={styles.hidden} /> : null}
            <td>
              {underRunner ? (
                <Selection
                  competitionId={overUnderMarket.competitionId}
                  eventId={overUnderMarket.eventId}
                  eventTypeId={overUnderMarket.eventTypeId}
                  handicap={underRunner.handicap}
                  legTypes={overUnderMarket.legTypes}
                  marketId={overUnderMarket.marketId}
                  marketStatus={overUnderMarket.marketStatus}
                  marketType={overUnderMarket.marketType}
                  odds={underRunner.winRunnerOdds}
                  runnerResultType={underRunner.resultType}
                  runnerStatus={underRunner.runnerStatus}
                  selectionId={underRunner.selectionId}
                  startTime={events[overUnderMarket.eventId].eventStartTime}
                />
              ) : null}
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
};

GameLines.marketTemplateType = 'EVENT';

export default GameLines;
